import React from 'react';
import tviklaMark from '../../assets/tvikla-mark.png';
import * as S from './TviklaHubDiagram.styles';

interface ModuleDef {
  label: string;
  icon: React.ReactNode;
}

// Icon paths copied verbatim from the design handoff (Tvikla Hero.dc.html)
// so the hub diagram renders the exact glyphs from the design file rather
// than an approximation.
const MODULES: ModuleDef[] = [
  {
    label: 'Production',
    icon: (
      <>
        <rect x={3} y={4} width={18} height={18} rx={1} />
        <path d="M3 10h18M8 2v4M16 2v4" />
      </>
    ),
  },
  {
    label: 'Marketplace',
    icon: (
      <>
        <circle cx={9} cy={20} r={1.4} />
        <circle cx={18} cy={20} r={1.4} />
        <path d="M2 3h2.5l2.6 11.2h11L21 7H6" />
      </>
    ),
  },
  {
    label: 'Logistics',
    icon: (
      <>
        <path d="M12 3v7M5 10V7h14v3M3 14l1.5 6h15L21 14z" />
        <path d="M3 14h18" />
      </>
    ),
  },
  {
    label: 'Tracking',
    icon: (
      <>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" />
        <circle cx={12} cy={10} r={2.6} />
      </>
    ),
  },
  {
    label: 'Insurance',
    icon: (
      <>
        <path d="M21.5 12a9.5 9.5 0 00-19 0z" />
        <path d="M12 12v7a2.2 2.2 0 004.4 0M12 2.5V5" />
      </>
    ),
  },
  {
    label: 'Customs',
    icon: (
      <>
        <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z" />
        <path d="M9 12l2.2 2.2L15.5 10" />
      </>
    ),
  },
  {
    label: 'Compliance',
    icon: <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z" />,
  },
  {
    label: 'Reconciliation & Risk',
    icon: (
      <>
        <path d="M3 17l6-6 4 4 8-8" />
        <path d="M15 7h6v6" />
      </>
    ),
  },
  {
    label: 'Documents',
    icon: (
      <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6M8 13h8M8 17h6" />
      </>
    ),
  },
  {
    label: 'Payments',
    icon: (
      <>
        <rect x={2} y={5} width={20} height={14} rx={1} />
        <path d="M2 10h20" />
      </>
    ),
  },
  {
    label: 'Messaging',
    icon: <path d="M21 12a8 8 0 01-11.6 7.1L4 21l1.9-5.2A8 8 0 1121 12z" />,
  },
];

// Geometry rule from the design spec: stage center (320, 320); node i angle =
// -90deg + i * (360 / moduleCount)deg, i = 0..n-1 clockwise from top; node
// center = center + 250 * (cos, sin); spoke runs from radius 110 (hub-disc
// edge + gap) to radius 192 (node edge - gap) on the same vector. Angle step
// stays dynamic so the ring re-spaces evenly whenever a module is added or
// removed.
const CENTER = 320;
const NODE_RADIUS = 250;
const NODE_CIRCLE_R = 54;
const HUB_DISC_R = 98;
const SPOKE_INNER = 110;
const SPOKE_OUTER = 192;
const ANGLE_STEP = 360 / MODULES.length;

function polar(angleDeg: number, radius: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad) };
}

const NODES = MODULES.map((m, i) => {
  const angle = -90 + i * ANGLE_STEP;
  const center = polar(angle, NODE_RADIUS);
  const inner = polar(angle, SPOKE_INNER);
  const outer = polar(angle, SPOKE_OUTER);
  return {
    ...m,
    centerX: center.x,
    centerY: center.y,
    left: center.x - NODE_CIRCLE_R,
    top: center.y - NODE_CIRCLE_R,
    spoke: { x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y },
  };
});

export interface TviklaHubDiagramProps {
  animate?: boolean;
}

export const TviklaHubDiagram: React.FC<TviklaHubDiagramProps> = ({ animate = true }) => {
  const playState = animate ? 'running' : 'paused';

  return (
    <S.Stage>
      <S.HubGlow />

      <S.SvgLayer viewBox="0 0 640 640">
        <defs>
          <radialGradient id="tvkSpoke" gradientUnits="userSpaceOnUse" cx={CENTER} cy={CENTER} r={196}>
            <stop offset="52%" stopColor="#F2691F" stopOpacity={0.78} />
            <stop offset="100%" stopColor="#F2691F" stopOpacity={0.2} />
          </radialGradient>
          <filter id="tvkHubShadow" x="-60%" y="-60%" width="220%" height="220%">
            <feDropShadow dx="0" dy="22" stdDeviation="26" floodColor="#0F1B2D" floodOpacity="0.16" />
          </filter>
        </defs>

        <g stroke="url(#tvkSpoke)" strokeWidth={3} strokeLinecap="round">
          {NODES.map((n) => (
            <line key={n.label} x1={n.spoke.x1} y1={n.spoke.y1} x2={n.spoke.x2} y2={n.spoke.y2} />
          ))}
        </g>

        <S.PulseGroup $playState={playState}>
          {NODES.map((n, i) => (
            <line
              key={n.label}
              x1={n.spoke.x2}
              y1={n.spoke.y2}
              x2={n.spoke.x1}
              y2={n.spoke.y1}
              style={{ animationDelay: `${i * (2.6 / MODULES.length)}s` }}
            />
          ))}
        </S.PulseGroup>

        <circle cx={CENTER} cy={CENTER} r={HUB_DISC_R} fill="#fff" filter="url(#tvkHubShadow)" />

        <rect x={CENTER - 98} y={436} width={196} height={32} fill="#0F1B2D" />
        <text x={CENTER} y={457.5} textAnchor="middle" fontSize={12.5} fontWeight={700} letterSpacing={1.8} fill="#fff">
          THE OPERATING CORE
        </text>
      </S.SvgLayer>

      {NODES.map((n) => (
        <S.ModuleNode key={n.label} style={{ left: n.left, top: n.top }}>
          <svg viewBox="0 0 24 24" width={26} height={26} fill="none" stroke="#F2691F" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            {n.icon}
          </svg>
          <S.ModuleLabel>
            {n.label === 'Reconciliation & Risk' ? <>Reconciliation<br />&amp; Risk</> : n.label}
          </S.ModuleLabel>
        </S.ModuleNode>
      ))}

      <S.HubTileImg src={tviklaMark} alt="Tvikla" />
      <S.OSBadge>
        <svg viewBox="0 0 66 66" width="100%" height="100%">
          <polygon points="24,17 47,17 43,22 20,22" fill="#F2691F" />
          <text x={33} y={50} textAnchor="middle" fontSize={29} fontWeight={800} fontStyle="italic" letterSpacing={-1} fill="#F2691F">
            OS
          </text>
        </svg>
      </S.OSBadge>
    </S.Stage>
  );
};
